import { useEffect, useRef, useState } from "react";
import { Plus, X, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteRefresh } from "@/context/SiteContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUploadField from "@/components/admin/ImageUploadField";

const SEO_PAGES = ["home", "products", "artists", "dealers", "about", "contact"];

const inputCls = "h-11 w-full border border-black/20 bg-white px-3 text-sm outline-none focus:border-gavra";
const areaCls = "w-full border border-black/20 bg-white p-3 text-sm outline-none focus:border-gavra";
const labelCls = "font-mono-hw text-[11px] uppercase tracking-widest text-black/50";

const SOCIAL_ICONS = ["instagram", "facebook", "youtube", "twitter", "tiktok", "linkedin", "spotify", "website"];

function Field({ label, value, onChange, placeholder, testid, area }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {area ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={area} placeholder={placeholder} className={`mt-1.5 ${areaCls}`} />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`mt-1.5 ${inputCls}`} />
      )}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="border border-black/10 p-5 md:p-6">
      <p className="font-heading text-lg font-bold uppercase tracking-tight">{title}</p>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export default function AdminContent() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const refreshSite = useSiteRefresh();
  const savedRef = useRef("");

  useEffect(() => {
    api.get("/settings").then((r) => { setData(r.data); savedRef.current = JSON.stringify(r.data); }).catch(() => toast.error("Failed to load content"));
  }, []);

  const dirty = data ? JSON.stringify(data) !== savedRef.current : false;

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (!data) {
    return <p className="font-mono-hw text-xs uppercase tracking-[0.3em] text-black/40">Loading content…</p>;
  }

  const home = data.home || {};
  const about = data.about || {};
  const contact = data.contact || {};
  const footer = data.footer || {};
  const socials = data.socials || [];
  const seo = data.seo || {};

  const upd = (section, key) => (val) => setData((d) => ({ ...d, [section]: { ...d[section], [key]: val } }));
  const setSeo = (page, key) => (val) => setData((d) => ({ ...d, seo: { ...d.seo, [page]: { ...(d.seo?.[page] || {}), [key]: val } } }));

  // Home marquee (string list)
  const setMarquee = (i, v) => setData((d) => ({ ...d, home: { ...d.home, marquee: d.home.marquee.map((m, mi) => (mi === i ? v : m)) } }));
  const addMarquee = () => setData((d) => ({ ...d, home: { ...d.home, marquee: [...(d.home.marquee || []), ""] } }));
  const rmMarquee = (i) => setData((d) => ({ ...d, home: { ...d.home, marquee: d.home.marquee.filter((_, mi) => mi !== i) } }));

  // Home features
  const setFeature = (i, key, v) => setData((d) => ({ ...d, home: { ...d.home, features: d.home.features.map((f, fi) => (fi === i ? { ...f, [key]: v } : f)) } }));

  // About chapters
  const setChapter = (i, key, v) => setData((d) => ({ ...d, about: { ...d.about, chapters: d.about.chapters.map((c, ci) => (ci === i ? { ...c, [key]: v } : c)) } }));
  const addChapter = () => setData((d) => ({ ...d, about: { ...d.about, chapters: [...(d.about.chapters || []), { n: "", title: "", body: "" }] } }));
  const rmChapter = (i) => setData((d) => ({ ...d, about: { ...d.about, chapters: d.about.chapters.filter((_, ci) => ci !== i) } }));

  // Socials
  const setSocial = (i, key, v) => setData((d) => ({ ...d, socials: d.socials.map((s, si) => (si === i ? { ...s, [key]: v } : s)) }));
  const addSocial = () => setData((d) => ({ ...d, socials: [...(d.socials || []), { label: "", icon: "website", href: "" }] }));
  const rmSocial = (i) => setData((d) => ({ ...d, socials: d.socials.filter((_, si) => si !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/settings", data);
      savedRef.current = JSON.stringify(saved || data);
      if (saved) setData(saved);
      await refreshSite();
      toast.success("Content saved", { description: "Your website has been updated." });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-hw text-[11px] uppercase tracking-[0.25em] text-gavra">Website CMS</p>
          <h1 className="mt-2 font-heading text-4xl font-black uppercase tracking-tight md:text-5xl">Content</h1>
        </div>
        <button data-testid="content-save-btn" onClick={save} disabled={saving || !dirty} className="flex items-center gap-2 bg-black px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {dirty && (
        <div data-testid="unsaved-badge" className="mt-4 flex items-center gap-2 border border-gavra bg-gavra/10 px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-gavra">
          <AlertTriangle className="h-4 w-4" /> You have unsaved changes
        </div>
      )}

      <Tabs defaultValue="home" className="mt-8">
        <TabsList className="flex h-auto flex-wrap justify-start gap-2 rounded-none bg-transparent p-0">
          {["home", "about", "contact", "footer", "social", "seo"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`content-tab-${t}`} className="rounded-none border border-black/20 px-4 py-2 text-[12px] font-bold uppercase tracking-wide data-[state=active]:bg-gavra data-[state=active]:text-white">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* HOME */}
        <TabsContent value="home" className="mt-6 space-y-5">
          <SectionCard title="Hero">
            <Field label="Eyebrow" testid="home-hero_eyebrow" value={home.hero_eyebrow} onChange={upd("home", "hero_eyebrow")} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Headline line 1" value={home.hero_line1} onChange={upd("home", "hero_line1")} />
              <Field label="Headline line 2" value={home.hero_line2} onChange={upd("home", "hero_line2")} />
              <Field label="Headline accent (orange)" value={home.hero_line3_accent} onChange={upd("home", "hero_line3_accent")} />
            </div>
            <Field label="Description" area={3} value={home.hero_description} onChange={upd("home", "hero_description")} />
            <ImageUploadField label="Hero image" testid="home-hero-image" value={home.hero_image} onChange={upd("home", "hero_image")} />
            <div className="grid grid-cols-1 gap-4 border-t border-black/10 pt-4 sm:grid-cols-2">
              <Field label="CTA button 1 text" testid="home-cta1_label" value={home.cta1_label} onChange={upd("home", "cta1_label")} />
              <Field label="CTA button 1 URL" testid="home-cta1_url" value={home.cta1_url} onChange={upd("home", "cta1_url")} placeholder="/products or https://…" />
              <Field label="CTA button 2 text" testid="home-cta2_label" value={home.cta2_label} onChange={upd("home", "cta2_label")} />
              <Field label="CTA button 2 URL" testid="home-cta2_url" value={home.cta2_url} onChange={upd("home", "cta2_url")} placeholder="/artists or https://…" />
            </div>
          </SectionCard>

          <SectionCard title="Scrolling Marquee">
            <div className="flex items-center justify-between">
              <span className={labelCls}>Phrases</span>
              <button type="button" onClick={addMarquee} className="flex items-center gap-1 text-[11px] font-bold uppercase text-gavra"><Plus className="h-3 w-3" /> Add</button>
            </div>
            <div className="space-y-2">
              {(home.marquee || []).map((m, i) => (
                <div key={i} className="flex gap-2">
                  <input value={m} onChange={(e) => setMarquee(i, e.target.value)} className={`${inputCls} h-10`} />
                  <button type="button" onClick={() => rmMarquee(i)} className="border border-black/20 px-3 hover:border-destructive hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Feature Cards (3)">
            {(home.features || []).map((f, i) => (
              <div key={i} className="grid grid-cols-1 gap-3 border-t border-black/10 pt-4 first:border-t-0 first:pt-0 sm:grid-cols-3">
                <Field label={`Card ${i + 1} title`} value={f.title} onChange={(v) => setFeature(i, "title", v)} />
                <div className="sm:col-span-2">
                  <Field label="Body" value={f.body} onChange={(v) => setFeature(i, "body", v)} />
                </div>
              </div>
            ))}
          </SectionCard>

          <SectionCard title="Section Headings">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Featured eyebrow" value={home.featured_eyebrow} onChange={upd("home", "featured_eyebrow")} />
              <Field label="Featured title" value={home.featured_title} onChange={upd("home", "featured_title")} />
            </div>
            <Field label="Artists eyebrow" value={home.artists_eyebrow} onChange={upd("home", "artists_eyebrow")} />
            <Field label="Artists title (use new lines)" area={3} value={home.artists_title} onChange={upd("home", "artists_title")} />
            <Field label="Artists description" area={3} value={home.artists_description} onChange={upd("home", "artists_description")} />
          </SectionCard>
        </TabsContent>

        {/* ABOUT */}
        <TabsContent value="about" className="mt-6 space-y-5">
          <SectionCard title="Header">
            <Field label="Eyebrow" value={about.eyebrow} onChange={upd("about", "eyebrow")} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Title" value={about.title} onChange={upd("about", "title")} />
              <Field label="Title accent (orange)" value={about.title_accent} onChange={upd("about", "title_accent")} />
            </div>
            <ImageUploadField label="Hero image" testid="about-hero-image" value={about.hero_image} onChange={upd("about", "hero_image")} />
            <Field label="Intro statement" area={3} value={about.intro} onChange={upd("about", "intro")} />
            <Field label="CTA title (use new lines)" area={2} value={about.cta_title} onChange={upd("about", "cta_title")} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="CTA button 1 label" testid="about-cta_primary_label" value={about.cta_primary_label} onChange={upd("about", "cta_primary_label")} />
              <Field label="CTA button 1 URL" testid="about-cta_primary_url" value={about.cta_primary_url} onChange={upd("about", "cta_primary_url")} placeholder="/products or https://…" />
              <Field label="CTA button 2 label" value={about.cta_secondary_label} onChange={upd("about", "cta_secondary_label")} />
              <Field label="CTA button 2 URL" testid="about-cta_secondary_url" value={about.cta_secondary_url} onChange={upd("about", "cta_secondary_url")} placeholder="/contact or https://…" />
            </div>
          </SectionCard>

          <SectionCard title="Manifesto Chapters">
            <div className="flex items-center justify-between">
              <span className={labelCls}>Chapters</span>
              <button type="button" data-testid="add-chapter-btn" onClick={addChapter} className="flex items-center gap-1 text-[11px] font-bold uppercase text-gavra"><Plus className="h-3 w-3" /> Add chapter</button>
            </div>
            <div className="space-y-4">
              {(about.chapters || []).map((c, i) => (
                <div key={i} className="border border-black/10 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-20 shrink-0">
                      <Field label="No." value={c.n} onChange={(v) => setChapter(i, "n", v)} />
                    </div>
                    <div className="flex-1">
                      <Field label="Title" value={c.title} onChange={(v) => setChapter(i, "title", v)} />
                    </div>
                    <button type="button" onClick={() => rmChapter(i)} className="mt-6 border border-black/20 px-3 py-2.5 hover:border-destructive hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-3">
                    <Field label="Body" area={2} value={c.body} onChange={(v) => setChapter(i, "body", v)} />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact" className="mt-6 space-y-5">
          <SectionCard title="Contact Page">
            <Field label="Eyebrow" value={contact.eyebrow} onChange={upd("contact", "eyebrow")} />
            <Field label="Title (use new lines)" area={2} value={contact.title} onChange={upd("contact", "title")} />
            <Field label="Description" area={3} value={contact.description} onChange={upd("contact", "description")} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Email shown" testid="contact-email-field" value={contact.email} onChange={upd("contact", "email")} />
              <Field label="Website shown" value={contact.web} onChange={upd("contact", "web")} />
            </div>
          </SectionCard>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer" className="mt-6 space-y-5">
          <SectionCard title="Footer">
            <Field label="Eyebrow" value={footer.eyebrow} onChange={upd("footer", "eyebrow")} />
            <Field label="Headline" area={2} value={footer.headline} onChange={upd("footer", "headline")} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Email" value={footer.email} onChange={upd("footer", "email")} />
              <Field label="Website" value={footer.web} onChange={upd("footer", "web")} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Copyright text (year added automatically)" testid="footer-copyright" value={footer.copyright} onChange={upd("footer", "copyright")} />
              <Field label="Bottom-right tagline" testid="footer-tagline" value={footer.tagline} onChange={upd("footer", "tagline")} />
            </div>
          </SectionCard>
        </TabsContent>

        {/* SOCIAL */}
        <TabsContent value="social" className="mt-6 space-y-5">
          <SectionCard title="Social Media Links">
            <div className="flex items-center justify-between">
              <span className={labelCls}>Links (shown in footer)</span>
              <button type="button" data-testid="add-social-btn" onClick={addSocial} className="flex items-center gap-1 text-[11px] font-bold uppercase text-gavra"><Plus className="h-3 w-3" /> Add link</button>
            </div>
            <div className="space-y-3">
              {socials.map((s, i) => (
                <div key={i} className="grid grid-cols-1 gap-3 border border-black/10 p-4 sm:grid-cols-12">
                  <div className="sm:col-span-3">
                    <label className={labelCls}>Icon</label>
                    <select data-testid={`social-icon-${i}`} value={s.icon || "website"} onChange={(e) => setSocial(i, "icon", e.target.value)} className={`mt-1.5 ${inputCls}`}>
                      {SOCIAL_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-3">
                    <Field label="Label" value={s.label} onChange={(v) => setSocial(i, "label", v)} />
                  </div>
                  <div className="sm:col-span-5">
                    <Field label="URL" testid={`social-href-${i}`} value={s.href} onChange={(v) => setSocial(i, "href", v)} placeholder="https://…" />
                  </div>
                  <div className="flex items-end sm:col-span-1">
                    <button type="button" onClick={() => rmSocial(i)} className="w-full border border-black/20 px-3 py-2.5 hover:border-destructive hover:text-destructive"><X className="mx-auto h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {socials.length === 0 && <p className="text-sm text-black/40">No social links yet.</p>}
            </div>
          </SectionCard>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="mt-6 space-y-5">
          <p className="text-sm text-black/50">Set the page title, meta description and social share (Open Graph) preview for each public page. These are used automatically for SEO and link previews.</p>
          {SEO_PAGES.map((page) => {
            const s = seo[page] || {};
            return (
              <SectionCard key={page} title={`${page} page`}>
                <Field label="Page title" testid={`seo-${page}-title`} value={s.title} onChange={setSeo(page, "title")} />
                <Field label="Meta description" area={2} testid={`seo-${page}-description`} value={s.description} onChange={setSeo(page, "description")} />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Open Graph title" value={s.og_title} onChange={setSeo(page, "og_title")} />
                  <Field label="Open Graph description" value={s.og_description} onChange={setSeo(page, "og_description")} />
                </div>
                <ImageUploadField label="Social share image (optional)" testid={`seo-${page}-ogimage`} value={s.og_image} onChange={setSeo(page, "og_image")} />
              </SectionCard>
            );
          })}
        </TabsContent>
      </Tabs>

      <div className="mt-8 flex justify-end">
        <button data-testid="content-save-btn-bottom" onClick={save} disabled={saving || !dirty} className="flex items-center gap-2 bg-black px-8 py-4 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}
